import React, { useState, useEffect } from 'react';

import LayerItem from './LayerItem.jsx'

import { useSelector } from 'react-redux'
import { selectLayers } from '../app/layers.js'
import { selectFilterableFields, get_display_value, selectFilters, selectOptionsWithSymbology, getAnnotationSymbology, SelectAnnotationsDataFields, allDescriptionKeys } from '../app/annotations.js'
import FeatureLegendTree from './FeatureLegendTree.jsx';

import BackupTableIcon from '@mui/icons-material/BackupTable';
import { CSVLink } from 'react-csv';
import { selectAnnotations, selectAnnotationFields } from '../app/annotations.js'
import ArrowCircleDownIcon from '@mui/icons-material/ArrowCircleDown';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import {selectMetricSystem,selectUnits, selectCoordSys, pos2InspectionCoordSys} from '../app/inspection.js'
import {mapToGeoJSON} from '../app/tools.js'
import {selectCurrentUserId} from "../app/auth.js"
import { useSearchParams } from 'react-router-dom';
import { normalizeColumnName } from "../app/formatHandlers/formatUtils.js";
import FeatureLabelHandler from './FeatureLabelHandler.jsx';
import AnnotationNumberingModal from './AnnotationNumberingModal.jsx';
import { Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography, Select, MenuItem, Checkbox, FormControlLabel, Button } from "@mui/material";

import { projectsApi, selectProjectId, selectProjects, setProjects } from '../app/projects.js';
import { useDispatch } from 'react-redux';
import apiClient from '../app/apiClient.js';

// export modal interface used to select labels list of fields and type of display in dxf file
const DxfExportModal = ({open, onClose, labelsLayerOptions, defaultCheckedNames, allFields, onExport}) => {
  const [selectLabelOption, setSelectLabelOption] = useState(labelsLayerOptions[0].key);
  const [checkedNames, setCheckedNames] = useState(defaultCheckedNames); 
  const [exportName, setExportName] = useState('')

  // handling text fields selection
  const handleCheckboxChange = (fieldTitle) => {
    setCheckedNames(prev =>
      prev.includes(fieldTitle)
        ? prev.filter(n => n !== fieldTitle)
        : [...prev, fieldTitle]
    );
  };

  // passing fields options to export function
  const handleExportClick = () => {
    onExport(selectLabelOption, checkedNames, exportName);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>DxF export - Options</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2">Name: (used as file name and title in cartouche)</Typography>
        <input
          type="text"s
          value={exportName}
          onChange={(e) => setExportName(e.target.value)}
          placeholder="Choose a Name"
          style={{ width: '100%', padding: 8, marginTop: 4, marginBottom: 16, boxSizing: 'border-box' }}
        />
        <Typography variant="subtitle2">Select a format for labels</Typography>
        
        <Box sx={{ bgcolor: "#f5f5f5", p: 0.5 }}>
          <Typography variant="body2" sx={{ display: 'block', lineHeight: 1.4 }}>
            <strong>Source Layer:</strong> Layer containing annotations, one per symbology key.
          </Typography>
          <Typography variant="body2" sx={{ display: 'block', lineHeight: 1.4 }}>
            <strong>Labels:</strong> Selected annotation fields exported as text, one text entity per label per annotation.
          </Typography>
        </Box>
        <Select
          fullWidth
          value={selectLabelOption}
          onChange={e => {
            const value = e.target.value;
            setSelectLabelOption(value);
          }}
          displayEmpty
          sx={{ mt: 2, mb: 2}}
        >
            {labelsLayerOptions.map(opt => (
              <MenuItem key={opt.key} value={opt.key}>
                {opt.value}
              </MenuItem>
            ))}
        </Select>
  
        <Typography variant="subtitle2">Fields to export as labels:</Typography>
        <Box
          sx={{
            maxHeight: 200,
            overflowY: 'auto',
            mt: 1,
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'flex-start',
            gap: 1,
          }}
        >
          {allFields.map(field => {
            const title = field.title || field.name;
            return (
              <label
                key={title}
                style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                }}
              >
                <input
                  type="checkbox"
                  checked={checkedNames.includes(title)}
                  onChange={() => handleCheckboxChange(title)}
                  style={{ marginRight: 8 }}
                />
                {title}
              </label>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleExportClick}>Export</Button>
      </DialogActions>
    </Dialog>
  );
}

function FiltersAndLayers(props) {
  const [searchParams] = useSearchParams();
  const inspectionId = searchParams.get("inspectionId")
  const viewId = searchParams.get("viewId")
  const layers = useSelector(selectLayers)
  const filters = useSelector(selectFilters)
  const units = useSelector(selectUnits)
  const filterableFields = useSelector(selectFilterableFields)
  const channel = window.orthophoto_bc_channel
  const annotations = useSelector(selectAnnotations)
  const annotationsFields = useSelector(selectAnnotationFields)
  const metricSystem = useSelector(selectMetricSystem);
  const selectedCoordSys = useSelector(selectCoordSys)
  const coloredOptionsFromKeys = useSelector(selectOptionsWithSymbology)
  const projectId = useSelector(selectProjectId);
  const projects = useSelector(selectProjects);
  const userId = useSelector(selectCurrentUserId)
  const annotationsDatafields =  useSelector(SelectAnnotationsDataFields)
  const allKeys = useSelector(allDescriptionKeys)
  const dispatch = useDispatch();

  const OUTPUTFIELDS = {
    projects: `id,name,userIds, adminIds, ownerIds, projectNumber,missions{id, name, inspections{id, name, views{id}, layers{type}}, potreeLayers,{id, name}},offices{id,name,userIds,projectId,missionIds,roles}`,
  }

  const defaultExportConfig = {
    // data field names  checked as default to be export as text field if they exist in the project
    forcedFields: [],
  };

  const [showModal, setShowModal] = useState(false); // display export modal
  const labelsLayerOptions = [
    { key: "1", value: "All texts from all labels in one layer" },
    { key: "2", value: "One label layer per source layer" },
    { key: "3", value: "One label layer per label and per source" },
  ];

  // Show "Photo viewer" link next to photo layers when we have inspection + view in the URL.
  const isPhotoLayer = (l) => l.type === "photo" || Array.isArray(l.photos);
  const hasPhotoLayer = layers.some(isPhotoLayer);
  const canOpenPhotoViewer = hasPhotoLayer && inspectionId && viewId;

  // get projects data from API
  useEffect(() => {
    if (projects.length === 0) {
      projectsApi.index({ userId }, OUTPUTFIELDS.projects, (result) => {
        dispatch(setProjects(result))
      })
    }
  }, [projects])

  const closeModal = () => setShowModal(false);

  // Generate JSON export (as a javascript object)
  const generate_json_export = () => {
    const geoJSONdata = { 
      "type": "FeatureCollection",
      "features":annotations.map((a) => {
        let output = {
          
          "type":"Feature",
          "geometry":mapToGeoJSON(a.geometry, (g) => {
            let pos = metricSystem(g)
            return [pos.x,pos.y]
          }),
          "properties":{
            "id": a.id
          }      
        }
        annotationsFields.keys.forEach((k) => {
          output["properties"][k.name] = get_display_value(k, a, allKeys);
        })
        
        if (a.position !== null) {
          const position = pos2InspectionCoordSys(selectedCoordSys, a.position)
          Object.entries(position).forEach(([pos_ref,v]) => {
            if (pos_ref === "length" || pos_ref === "area")
              output[`${pos_ref}`] = {unit: v.unit, value: v.value}
          })
        }
        return output;
      })
    }
    return geoJSONdata;
  }

  // Generate CSV export (as a string)
  const generate_csv_export = () => {
    // Columns that are injected manually in this export.
    // If the View (DB) contains a field with the same column name, drop it from DB columns to avoid collisions.
    const reservedColumnNames = new Set(
      [
        "id",
        "geometry",
        "geometryName",
        "NumPoint",
        "N° Point",
        "X",
        "Y",
        "length",
        "area",
      ].map(normalizeColumnName)
    );

    // Rule: fields coming from the View (DB) are stored inside annotation.data, so a user could create a data-field
    // named like a default export column (e.g. "geometry", "X", "Y", etc.) and accidentally override it.
    // To prevent subtle export corruption (wrong geometry type / wrong X,Y / wrong point count), we ALWAYS give
    // priority to the auto-generated columns, and we drop any DB columns that collide by name.
    const dbKeysForCsv = (annotationsFields?.keys || []).filter((k) => {
      const n = normalizeColumnName(k?.name || k?.title);
      return n && !reservedColumnNames.has(n);
    });

    // Headers
    const headers = [
      {label: "id", key:"id"},
      {label: "geometry", key:"geometry"},
      {label: "N° Point", key:"NumPoint"},
      {label: `X (${selectedCoordSys?.x?.unit ?? "m"})`, key:"X"},
      {label: `Y (${selectedCoordSys?.y?.unit ?? "m"})`, key:"Y"},
      ...dbKeysForCsv.map(c => ({ label: c.title, key: c.name })),
      selectedCoordSys.x !== undefined && {
        label: `length (${selectedCoordSys.x.unit})`,
        key: `length`
      },
      selectedCoordSys.x !== undefined && {
          label: `area (${selectedCoordSys.x.unit}²)`,
          key: `area` 
      }
    ];

    const computePropertiesForFirstRow = (a) => {
      const output = { id: a.id };

      dbKeysForCsv.forEach((k) => {
        output[k.name] = get_display_value(k, a, allKeys);
        // Replace endofline characters by spaces, otherwise the CSV file will be corrupted
        if (typeof output[k.name] === "string") output[k.name] = output[k.name].replace(/[\n\r]/g, " ");
      });

      if (a.position !== null) {
        const position = pos2InspectionCoordSys(selectedCoordSys, a.position);
        Object.entries(position).forEach(([pos_ref, v]) => {
          if (pos_ref === "length" || pos_ref === "area") {
            output[`${pos_ref}`] = v.value;
          }
        });
      }

      return output;
    };

    const annotationToGeometryTypeAndPoints = (a) => {
      const geo = mapToGeoJSON(a.geometry, (g) => {
        // Export raw reference coordinates so CSV re-import works reliably
        // even outside of the ViewEditor where coordSys may differ.
        return [g[0], g[1]];
      });

      if (!geo || !geo.type) return { geometryType: "", points: [] };

      if (geo.type === "Point") {
        return { geometryType: "Point", points: [geo.coordinates] };
      }
      if (geo.type === "LineString") {
        return { geometryType: "LineString", points: geo.coordinates || [] };
      }
      if (geo.type === "Polygon") {
        // Export the outer ring as vertices (common convention)
        const outer = Array.isArray(geo.coordinates) ? geo.coordinates[0] : [];
        return { geometryType: "Polygon", points: outer || [] };
      }

      return { geometryType: geo.type, points: [] };
    };

    const toBusinessGeometry = (geometryType) => {
      if (!geometryType) return "";
      if (geometryType === "Point") return "PONCTUEL";
      if (geometryType === "LineString") return "LINEAIRE";
      if (geometryType === "Polygon") return "SURFACIQUE";
      return "";
    };

    // Prepare rows: one CSV row per point in the annotation geometry.
    // Only the first point row includes properties/fields; subsequent rows keep those empty.
    //
    // Example (an annotation with 3 points):
    // id;geometry;N° Point;X (unit);Y (unit);Category;Level;Title;Message;Fixed?
    // 69e8...;LINEAIRE;1;20903.78;-10.09;Sample Category 2;0;;;false
    // 69e8...;LINEAIRE;2;20904.10;-10.15;;;;;
    // 69e8...;LINEAIRE;3;20904.55;-10.20;;;;;
    const data = [];
    for (const a of annotations) {
      const { geometryType, points } = annotationToGeometryTypeAndPoints(a);
      if (!points || points.length === 0) continue;

      const firstRowProps = computePropertiesForFirstRow(a);

      points.forEach((pt, idx) => {
        const row = {
          id: a.id,
          geometry: toBusinessGeometry(geometryType),
          NumPoint: idx + 1,
          X: Array.isArray(pt) ? pt[0] : "",
          Y: Array.isArray(pt) ? pt[1] : "",
        };

        if (idx === 0) {
          Object.assign(row, firstRowProps);
        }

        data.push(row);
      });
    }

    // Make csv
    // Function to generate CSV with custom separator and enclosing character
    function generateCSV(headers, data, separator = ";", enclosingCharacter = "") {
      // 1. Header row
      const headerRow = headers.map(h => `${enclosingCharacter}${h.label}${enclosingCharacter}`).join(separator);

      // 2. Data rows
      const rows = data.map(row => {
        return headers
          .map(h => {
            let value = row[h.key] ?? "";
            // Only escape separator if needed (optional)
            if (typeof value === "string" && value.includes(separator)) {
              // Wrap value if it contains separator, even if enclosingCharacter is empty
              value = `${enclosingCharacter}${value}${enclosingCharacter}`;
            }
            return value;
          })
          .join(separator);
      });

      // 3. Combine header and data
      return [headerRow, ...rows].join("\n");
    }
    return generateCSV(headers, data, ";", "");
  }

  const ExportButtons = () => {
    const [downloadingDxF,setDownloadingDxF] = useState(false) // When the Dxf downloading is ongoing (and the button is disabled)

    // If there are no fields (loading pending), return empty html
    if (annotationsFields.keys.length === 0){
      return <div></div>;
    }

    const downloadDxF = (datas) => {
      // Set the downloading as started
      setDownloadingDxF(true);
      const { selectLabelOption, selectedFields, exportName } = datas;
      const coloredKeys = [];
      const legendKeys = [];

      // formate an object for each key used for layers in the next step
      for (const opt of coloredOptionsFromKeys) {
        const baseData = {
          id: opt.id,
          text: opt.text,
          keyName: opt.keyName,
          title: opt.title,
          ...(opt.hasOwnProperty("color") ? { color: opt.color } : {}),
          ...(opt.hasOwnProperty("symbol") ? { symbol: opt.symbol } : {}),
          ...(opt.hasOwnProperty("fill_pattern") ? { fill_pattern: opt.fill_pattern } : {}),
        };
      
        coloredKeys.push({ ...baseData, entities: [] });
        legendKeys.push({ ...baseData }); 
      }

      // Fields to export as labels (one logical key per name; submenus merged via annotationsFields)
      const selectedKeys = annotationsFields.keys.filter((k) =>
        selectedFields.map((f) => f.name).includes(k.name)
      );

      /*
        handling labels to dislay in dxf
        3 options possible:
          - all labels in th same layer
          - all label in their annotation layer
          - a layer per label per symbology layer
      */
      if (selectLabelOption === "1") {
        // pushin a text layer containing all the annotations data per label
        coloredKeys.push({
          id: "Labels",
          name: "Labels",
          text: "Labels",
          labels: [],
        });
      } else if (selectLabelOption === "2") {
        // add a labels in each already existing layer
        coloredKeys.forEach((key) => {
          key.labels = []
        })
      } else if (selectLabelOption === "3") {
        // pushing a new text layer for each selectedKey and per geometry layer
        const newTextLayers = [];
        coloredKeys.forEach((layer) => {
          selectedKeys.forEach((key) => {
            newTextLayers.push({
              id: `${layer.id}_${key.id}`,
              text: `${layer.text}_${key.title}`,
              ...(layer.hasOwnProperty('color') && { color: layer.color }),
              baseLayerId: layer.id,
              labelKey: key.name,
              labels: []
            });
          });
        });
        coloredKeys.push(...newTextLayers);
      }

      // Make layers
      let inputs = {
        projectId: projectId,
        "layers": coloredKeys.map(el => ({
          ...el,
        }))
      };
    
      let layer_others = null;
    
      // add annotations in the right layers
      for (let ann of annotations) {
        let symbology = getAnnotationSymbology(ann, coloredOptionsFromKeys);
    
        if (symbology === null) {
          if (layer_others === null) {
            layer_others = {
              "text": "Others",
              "color": [255, 255, 255],
              "entities": []
            };
            inputs.layers.push(layer_others);
          }
          layer_others.entities.push(ann);
        } else {
          const tmp_ann = {
            geometry: ann.geometry,
            geometryName: ann.geometryName,
            ...(symbology.symbol ? { symbol: symbology.symbol } : {}),
            ...(symbology.fill_pattern ? { fill_pattern: symbology.fill_pattern } : {})
          };
    
          let the_layer = null;
          the_layer = inputs.layers.filter((el) => el.id === symbology.id && el.keyName === symbology.keyName)[0];
          
          if (selectLabelOption === "1") {
            // adding all annotation data per label in the same layer
            const layer = coloredKeys.find((l) => l.id == "Labels");
            selectedKeys.forEach((k) => {
              layer.labels.push({
                geometry: ann.geometry,
                geometryName: ann.geometryName,
                value: get_display_value(k, ann, allKeys)
              })
            })
          } else if (selectLabelOption === "2") {
            // add each label value for the annotation as a text to add in the same layer
            selectedKeys.forEach((k) => {
              the_layer.labels.push({
                geometry: ann.geometry,
                geometryName: ann.geometryName,
                value: get_display_value(k, ann, allKeys)
              });
            });
          } else if (selectLabelOption === "3") {
            // find the specific layer per label to add the annotation
            selectedKeys.forEach((labelKey) => {
              const textLayerId = `${symbology.id}_${labelKey.id}`;
              const textLayer = inputs.layers.find(l => l.id === textLayerId);
              if (textLayer) {
                textLayer.labels.push({
                  geometry: ann.geometry,
                  geometryName: ann.geometryName,
                  value: get_display_value(labelKey, ann, allKeys)
                });
              }
            });
          }
          // pushing the annotation in it's geometry layer
          if (the_layer) {
            the_layer.entities.push(tmp_ann);
          }
        }
      }

      // cleaning object fields before sending it
      // ensure each layer has a non-empty name (text) for DXF export
      inputs.layers = inputs.layers.map((l) => ({
        color: l.color,
        text: l.text && String(l.text).trim() !== "" ? l.text : "DEFAULT_NAME",
        ...(l.hasOwnProperty("entities") ? { entities: l.entities } : {}),
        ...(l.hasOwnProperty("labels") ? { labels: l.labels } : {}),
      }));

      // adding manually cartouche part
      inputs.layers.push({
        text: "cartouche",
        color: [255, 255, 255],
        metadata: {
          fileName: exportName,
          signature: {},
        },
        legendKeys,
      });

      // Update layer names (in the DxF format, 2 layers can't have the same name)
      // Group layers by name:
      let layers_by_name = inputs.layers.reduce((acc, layer) => {
        if (acc[layer.text] === undefined) acc[layer.text] = [];
        acc[layer.text].push(layer);
        return acc;
      }, {}); 
      // Update layer names for cases where >1 layer have the same name:
      for (let layer_name of Object.keys(layers_by_name)){
        if (layers_by_name[layer_name].length > 1){
          for (let i=0; i<layers_by_name[layer_name].length; i++){
            layers_by_name[layer_name][i].text = layers_by_name[layer_name][i].text + ` (${i+1})`;
          }
        }
      }  

      apiClient.dxf_export(inputs)
        .then(response => {
          const element = document.createElement('a');
          element.setAttribute("href", URL.createObjectURL(new Blob([response.data], { type: "image/x-dxf" })));
          element.setAttribute('download', "export.dxf");
          element.style.display = 'none';
          document.body.appendChild(element);
          element.click();
          document.body.removeChild(element);
          setDownloadingDxF(false);
        })
        .catch(err => {
          console.error("Erreur lors de l'export DXF: ", err);
          setDownloadingDxF(false);
        });
    };

    const ensureFieldExists = (fields, name) => {
      return fields.some(f => f.name === name || f.title === name);
    }

    const allFields = [
      ...annotationsDatafields, // get existing fiels
      ...defaultExportConfig.forcedFields // add default selected fields (handle special cases like num_defaut not always in view)
        .filter(f => !ensureFieldExists(annotationsDatafields, f))
        .map(f => ({ name: f, title: f }))
    ];

    const handleExport = (newSelectLabelOption, selectedFieldTitles, exportName) => {
      const selectedFields = Array.from(new Set([
        ...selectedFieldTitles,
        ...defaultExportConfig.forcedFields
      ]));
    
      // get data fields selected in form
      const filteredDatafields = annotationsDatafields.filter(field =>
        selectedFields.includes(field.title)
      );
    
      // Do export
      downloadDxF({
        selectLabelOption: newSelectLabelOption,
        selectedFields: filteredDatafields,
        exportName,
      });
    };


    return <span>
        <a href="" target="_blank" rel="noopener noreferrer" download="geojson.json" onClick={(e) => {
          const geoJSONdata = generate_json_export();
          const blob = new Blob([JSON.stringify(geoJSONdata,null, 2)], { type: "text/plain" });
          e.currentTarget.setAttribute("href", URL.createObjectURL(blob));
        }}>
        <button><ArrowCircleDownIcon/>JSON</button>
      </a>


      <a href="" target="_blank" rel="noopener noreferrer" download="export.csv" onClick={(e) => {
          const csv = generate_csv_export();
          const blob = new Blob(
            ["\uFEFF" + csv], // prepend BOM so that Excel detects it's UTF-8
            { type: "text/plain" }
          );
          e.currentTarget.setAttribute("href", URL.createObjectURL(blob));
        }}>
        <button><ArrowCircleDownIcon/>CSV</button>
      </a>

    <a href="#" onClick={(e) => {
      e.preventDefault();
      setShowModal(prev => !prev);
    }}>
      <button disabled={downloadingDxF}>
        {downloadingDxF ? <HourglassTopIcon /> : <ArrowCircleDownIcon />} DxF
      </button>
    </a>
    {allFields && labelsLayerOptions &&
      <DxfExportModal
        open={showModal}
        onClose={closeModal}
        labelsLayerOptions={labelsLayerOptions}
        defaultCheckedNames={defaultExportConfig.forcedFields}
        allFields={allFields}
        onExport={handleExport}
      />
    }
    </span>
  }


  const numberFieldsForNumbering = annotationsFields.keys.filter(
    (k) => k.type === 'number'
  );

  return <div>
     <button onClick={() => {window.open(`/annotations?inspectionId=${inspectionId}&channel=${channel}`, "Table", "height=800,width=800");
}}>
      <BackupTableIcon/> 
          Defects
    </button>
    <AnnotationNumberingModal numberFields={numberFieldsForNumbering} />
    { projectId && <ExportButtons/> }


    <h3>Filter and layers</h3>
    <div className="layers-box">
      {[...layers].reverse().map((el) => (
        <LayerItem
          key={"layer-item-" + el.id}
          id={el.id}
          name={el.name}
          checked={el.selected}
          colorBar={el.colorBar}
          photoViewerHref={
            canOpenPhotoViewer && isPhotoLayer(el)
              ? `/photo-viewer?inspectionId=${inspectionId}&viewId=${viewId}&layerId=${el.id}`
              : null
          }
        />
      ))}
    </div>
    <FeatureLabelHandler annotationsFields={annotationsFields}/>

    <FeatureLegendTree fields={filterableFields} filters={filters} />
    <br/>
    <br/>
    <br/>
  </div>
}

export default FiltersAndLayers;